import React, { useMemo, useRef } from 'react'
import type { PlotViewport } from '../contract'
import { usePlotGraphicBudget } from '../_shared/usePlotGraphicBudget'

import { ChartLabel } from '../_shared/ChartLabel'
import { ChartLabelRenderMode, chartLabelFullText, chartLabelHintIndex } from '../_shared/ChartLabelFullText'
import { OtherToggle, OtherToggleLabels, isOtherExpandable, useOtherExpand } from '../_shared/OtherExpand'
import { SectionHeader, sectionOpenedAt } from '../_shared/SectionHeader'
import { SegmentBar, defaultLabelMinSharePct } from '../_shared/SegmentBar'
import { ShareUnit } from '../_shared/ShareUnitToggle'
import { StoredPlotPayload } from '../contract'
import {
  categoryFills,
  fillCategoricalTemplate,
  foldedFills,
  formatShare,
  nameTitle,
  foldedRowBars,
  otherFold,
  rowOpensOther,
  rowsShowImages,
  slotColor,
} from './style'

import {
  CategoricalBar,
  CategoricalRow,
  CategoricalShareData,
  PlotSize,
} from '@digital-twins/slide-schemas/generated/plot-types'

/**
 * The categorical share kinds (plots plan L), drawn from Python's payload only.
 *
 * - `stacked-100-bars`: one bar per row, each category a segment as wide as
 *   its unrounded share (`exactShare`), never re-normalised, so a single
 *   answer's row fills the bar; each segment prints its rounded share.
 * - `grouped-bars`: a group per category (its label above), one bar per row
 *   (lane) inside it, painted in the row's slot. For multi-response data,
 *   whose shares of one row sum past 100. With `groupedBy: row` (wave 7 f1)
 *   the groups are the rows, in their stamped sections (`data.groups`), one
 *   bar per category in the category's slot, the Other row's bars neutral.
 *
 * Nothing here ranks, rounds, rolls up or crowns. Every chrome word is a label
 * the payload carries; a missing one is not drawn.
 *
 * Sizes: `slide` keeps the matrix-categorical slide's row metrics (the slide
 * places rows and legend at its own geometry); `report` and `card` are the
 * responsive survey-tab / card look (rows then legend, low-base marks).
 *
 * "Other" opens (decision 23): with `otherOpen`, the Other mark is replaced by
 * the categories Python folded into it (`foldedCategories`, each row's
 * `otherShares`), in neutral tints. Closed, nothing changes.
 */

export type CategoricalShareKind = 'stacked-100-bars' | 'grouped-bars'

export type CategoricalSharePayload =
  | StoredPlotPayload<'stacked-100-bars'>
  | StoredPlotPayload<'grouped-bars'>

export type CategoricalSharePlotData = StoredPlotPayload<'stacked-100-bars'>['data']

export interface RenderRowLabelArgs {
  row: CategoricalRow
  index: number
  style: React.CSSProperties
  /** The shared truncation rule's (i) for this row (plots plan LB). */
  chartLabel: { hintIndex: number; fullText?: string | null }
}

interface SharedProps {
  data: CategoricalSharePlotData
  size: PlotSize
  locale?: string
  /**
   * A value label sits inside a stacked segment at >= this % of the track.
   * Default: today's slide rule (7, or 14 past four categories).
   */
  labelMinSharePct?: number
  /** The deck passes an editable text; other surfaces draw the label. */
  renderRowLabel?: (args: RenderRowLabelArgs) => React.ReactNode
  /** Test-id prefix: the deck keeps its slide's ids (`matrix-categorical`). */
  testIdPrefix?: string
  /**
   * `static` (the PDF pass) draws no (i) beside a shortened label; every other
   * surface shows the source text on hover or focus (C2).
   */
  renderMode?: ChartLabelRenderMode
  /** Draw what "Other" folded in its place (decision 23); the host owns the link. */
  otherOpen?: boolean
  /** Print each stacked segment's people beside its share ("30% · 12"), from the stamped `count`. */
  showCounts?: boolean
  /** Survey tab: print the stamped count instead of the share. A missing count stays the share. */
  shareUnit?: ShareUnit
  /** Survey tab: the 150ms tooltip. Slides keep the browser tooltip. */
  fastHover?: boolean
}

const GRAY = 'var(--slide-text-muted)'
const TEXT = 'var(--slide-cover-bg, #000000)'
const TRACK = 'var(--slide-neutral-light, #ebebeb)'

/** Today's slide density ladder, keyed on the row count only. */
export function stackedRowLayout(rowCount: number) {
  const dense = rowCount > 8
  return {
    rowGap: dense ? 5 : rowCount > 5 ? 10 : 18,
    rowLabelH: dense ? 18 : 20,
    rowBarMt: dense ? 3 : 7,
    rowBarH: dense ? 15 : 28,
  }
}

function printedFigure(
  share: number,
  count: number | null | undefined,
  decimals: number,
  locale: string | undefined,
  shareUnit: ShareUnit | undefined,
  showCounts: boolean,
): string {
  const shareText = formatShare(share, decimals, locale)
  if (shareUnit === 'count') {
    if (typeof count === 'number') {
      // Grouped like every other plot's count (choice bars, histogram, likert).
      return new Intl.NumberFormat(locale || undefined, { maximumFractionDigits: 0 }).format(count)
    }
    return shareText
  }
  if (showCounts && typeof count === 'number') {
    return `${shareText} · ${count}`
  }
  return shareText
}

function Thumb(props: { row: CategoricalRow; index: number; height: number; testIdPrefix: string }) {
  const { row, index, height, testIdPrefix } = props

  return (
    <span
      data-testid={`${testIdPrefix}-thumb-${index}`}
      style={{ position: 'relative', width: Math.round(height * 1.4), height, flexShrink: 0, borderRadius: 3, overflow: 'hidden', background: TRACK }}
    >
      {row.imageUrl ? (
        <img src={row.imageUrl} alt={row.label} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
      ) : null}
      {row.previewKind === 'video' ? (
        <span
          data-testid={`${testIdPrefix}-play-${index}`}
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <span style={{ width: 0, height: 0, borderStyle: 'solid', borderWidth: '4px 0 4px 7px', borderColor: 'transparent transparent transparent #ffffff', filter: 'drop-shadow(0 0 1px rgba(0,0,0,0.6))' }} />
        </span>
      ) : null}
    </span>
  )
}

function RowName(props: {
  row: CategoricalRow
  index: number
  size: PlotSize
  height: number
  showImages: boolean
  lowBaseLabel?: string
  renderRowLabel?: SharedProps['renderRowLabel']
  testIdPrefix: string
  renderMode?: ChartLabelRenderMode
}) {
  const { row, index, size, height, showImages, lowBaseLabel, renderRowLabel, testIdPrefix, renderMode } = props
  const style: React.CSSProperties = { fontSize: size === 'slide' ? 16 : 13, color: TEXT, minWidth: 0, flex: 1 }

  const label = renderRowLabel ? (
    renderRowLabel({ row, index, style, chartLabel: { hintIndex: chartLabelHintIndex(0, 'row', index), fullText: row.fullLabel } })
  ) : (
    <ChartLabel
      data-testid={`${testIdPrefix}-row-label`}
      text={row.label}
      fullText={row.fullLabel}
      hintIndex={chartLabelHintIndex(0, 'row', index)}
      maxLines={size === 'slide' ? 1 : 2}
      renderMode={renderMode}
      title={nameTitle(row)}
      style={style}
    />
  )
  const lowBase =
    size !== 'slide' && row.lowBase && lowBaseLabel ? (
      <span data-testid={`${testIdPrefix}-low-base-${index}`} style={{ fontSize: 11, color: GRAY, flexShrink: 0 }}>
        {lowBaseLabel}
      </span>
    ) : null

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: height, minWidth: 0 }}>
      {showImages ? <Thumb row={row} index={index} height={Math.min(20, height)} testIdPrefix={testIdPrefix} /> : null}
      {label}
      {lowBase}
    </div>
  )
}

function EmptyRow(props: { label?: string; height: number; testId: string }) {
  const { label, height, testId } = props

  if (!label) {
    return <div data-testid={testId} style={{ height }} />
  }
  return (
    <div data-testid={testId} style={{ height, display: 'flex', alignItems: 'center', fontSize: 11, color: GRAY }}>
      {label}
    </div>
  )
}

/** The stacked rows (labels above their bars). The legend is drawn apart. */
export function StackedHundredBarsRows(props: SharedProps) {
  const { data, size, locale, renderRowLabel, testIdPrefix = 'stacked-100-bars', renderMode, otherOpen = false, showCounts = false, shareUnit, fastHover = false } = props
  // Category list, not the pieces one row draws. A row that omits a category must not change the threshold.
  const labelMinSharePct = props.labelMinSharePct ?? defaultLabelMinSharePct(data.categories.length)

  const fills = categoryFills(data)
  const fold = otherOpen ? otherFold(data) : null
  const folded = fold ? foldedFills(fold.folded.map((entry) => entry.id)) : null
  const foldedById = new Map((fold?.folded ?? []).map((entry) => [entry.id, entry]))
  const layout = stackedRowLayout(data.rows.length)
  const barH = size === 'slide' ? layout.rowBarH : 20
  const showImages = rowsShowImages(data.rows)

  return (
    <div data-testid={`${testIdPrefix}-rows`} style={{ display: 'flex', flexDirection: 'column', gap: size === 'slide' ? layout.rowGap : 12, minWidth: 0 }}>
      {data.rows.map((row, rowIndex) => (
        <div key={row.id} data-testid={`${testIdPrefix}-row`} style={{ minWidth: 0 }}>
          <RowName
            row={row}
            index={rowIndex}
            size={size}
            height={size === 'slide' ? layout.rowLabelH : 18}
            showImages={showImages}
            lowBaseLabel={data.labels.plot_low_base}
            renderRowLabel={renderRowLabel}
            testIdPrefix={testIdPrefix}
            renderMode={renderMode}
          />
          {row.bars.length === 0 ? (
            <EmptyRow label={data.labels.lane_merged_empty_lane} height={barH} testId={`${testIdPrefix}-empty-${rowIndex}`} />
          ) : (
            <SegmentBar
              testId={`${testIdPrefix}-row-bars`}
              heightPx={barH}
              radiusPx={2}
              labelMinSharePct={labelMinSharePct}
              hover={fastHover ? 'fast' : 'native'}
              rootStyle={{ marginTop: size === 'slide' ? layout.rowBarMt : 4 }}
              segments={openedBars(row, fold).map(({ bar, isFolded, foldedIndex }) => {
                const fill = isFolded ? folded?.get(bar.categoryId) : fills.get(bar.categoryId)
                const category = isFolded ? foldedById.get(bar.categoryId) : data.categories.find((entry) => entry.id === bar.categoryId)
                const printed = printedFigure(bar.share, bar.count, data.decimals, locale, shareUnit, showCounts)
                return {
                  key: isFolded ? `${bar.categoryId}-${foldedIndex}` : bar.categoryId,
                  share: bar.share,
                  // legacy reader only: a plot stored before `exactShare` draws the printed share.
                  exactShare: bar.exactShare ?? bar.share,
                  fill: fill?.fill ?? '',
                  text: fill?.text ?? '',
                  printed,
                  title: category ? `${nameTitle(category)}: ${printed}` : printed,
                  testId: isFolded ? `${testIdPrefix}-other-segment-${rowIndex}-${foldedIndex}` : `${testIdPrefix}-bar`,
                  // An opened Other holds many thin segments: they keep a hairline, not 12 px.
                  minWidthPx: bar.share > 0 ? (isFolded ? 2 : 12) : 0,
                  dataAttrs: row.topCategoryIds.includes(bar.categoryId) ? { 'data-top': 'true' } : undefined,
                }
              })}
            />
          )}
        </div>
      ))}
    </div>
  )
}

/**
 * A row's bars as drawn: opened, the Other bar is replaced in place by the
 * categories it folded (the row's positional `otherShares` / `otherCounts`,
 * as Python stamped them); a folded category the row has no figure for draws nothing.
 */
function openedBars(
  row: CategoricalRow,
  fold: { otherId: string; folded: Array<{ id: string }> } | null,
): Array<{ bar: CategoricalBar; isFolded: boolean; foldedIndex: number }> {
  const opened = fold ? foldedRowBars(row, fold.folded) : []
  return row.bars.flatMap((bar): Array<{ bar: CategoricalBar; isFolded: boolean; foldedIndex: number }> =>
    fold && bar.categoryId === fold.otherId && opened.some((entry) => entry !== null)
      ? opened.flatMap((entry, foldedIndex) => (entry ? [{ bar: entry, isFolded: true, foldedIndex }] : []))
      : [{ bar, isFolded: false, foldedIndex: -1 }],
  )
}

/** Legend of the categories (stacked) or of the rows (grouped). */
export function CategoricalShareLegend(props: {
  data: CategoricalSharePlotData
  kind: CategoricalShareKind
  size: PlotSize
  testIdPrefix?: string
  renderMode?: ChartLabelRenderMode
  /** Stacked only: list what "Other" folded in its place (decision 23). */
  otherOpen?: boolean
}) {
  const { data, kind, size, testIdPrefix = kind, renderMode, otherOpen = false } = props

  const fills = categoryFills(data)
  const fold = otherOpen && kind !== 'grouped-bars' ? otherFold(data) : null
  const folded = fold ? foldedFills(fold.folded.map((entry) => entry.id)) : null
  const byRow = kind === 'grouped-bars' && data.groupedBy === 'row'
  const entries =
    kind === 'grouped-bars' && !byRow
      ? data.rows.map((row, index) => ({
          id: row.id,
          label: row.label,
          fullText: row.fullLabel,
          hintIndex: chartLabelHintIndex(data.rows.length, 'row', index),
          title: nameTitle(row),
          color: slotColor(row.colorSlot),
          muted: row.bars.length === 0,
          base: null as string | null,
        }))
      : data.categories.map((category, index) => ({
          id: category.id,
          label: category.label,
          fullText: category.fullLabel,
          hintIndex: chartLabelHintIndex(data.rows.length, 'category', index),
          title: nameTitle(category),
          color: byRow ? slotColor(category.colorSlot) : fills.get(category.id)?.fill,
          muted: false,
          // By row, each category's n (the base its shares are of) prints in the shared format.
          base: byRow && typeof category.base === 'number' && data.labels.plot_base_n
            ? fillCategoricalTemplate(data.labels.plot_base_n, { n: category.base })
            : null,
        }))
  const emptyLabel = data.labels.lane_merged_empty_lane

  return (
    <div
      data-testid={`${testIdPrefix}-legend-entries`}
      style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-start', columnGap: 18, rowGap: size === 'slide' ? 8 : 6 }}
    >
      {entries.map((entry, index) =>
        fold && entry.id === fold.otherId ? (
          <span key={entry.id} data-testid={`${testIdPrefix}-other-members`} style={{ display: 'contents' }}>
            {fold.folded.map((member, memberIndex) => (
              <div key={member.id} data-testid={`${testIdPrefix}-other-member-${memberIndex}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: folded?.get(member.id)?.fill, flexShrink: 0 }} />
                <ChartLabel
                  text={member.label}
                  fullText={member.fullLabel}
                  hintIndex={foldedHintIndex(data, memberIndex)}
                  hintSize={10}
                  maxLines={size === 'slide' ? 1 : 2}
                  renderMode={renderMode}
                  title={nameTitle(member)}
                  style={{ fontSize: size === 'slide' ? 10 : 11, color: GRAY }}
                />
              </div>
            ))}
          </span>
        ) : (
          <div key={entry.id} data-testid={`${testIdPrefix}-legend-entry-${index}`} title={entry.title} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <span aria-hidden="true" style={{ width: 6, height: 6, borderRadius: '50%', background: entry.color, flexShrink: 0 }} />
            <ChartLabel
              data-testid={`${testIdPrefix}-legend-label`}
              text={legendText(entry.label, entry.muted, emptyLabel)}
              fullText={legendFullText(entry, emptyLabel)}
              hintIndex={entry.hintIndex}
              hintSize={10}
              maxLines={size === 'slide' ? 1 : 2}
              renderMode={renderMode}
              title={entry.title}
              style={{ fontSize: size === 'slide' ? 10 : 11, color: GRAY }}
            />
            {entry.base ? (
              <span data-testid={`${testIdPrefix}-legend-base`} style={{ fontSize: size === 'slide' ? 10 : 11, color: GRAY, flexShrink: 0 }}>
                {entry.base}
              </span>
            ) : null}
          </div>
        ),
      )}
    </div>
  )
}

function legendText(label: string, isMuted: boolean, emptyLabel?: string | null): string {
  return isMuted && emptyLabel ? `${label}: ${emptyLabel}` : label
}

/**
 * The (i) value for a legend entry: the source name when the legend prints a
 * shorter one, compared against the printed name (not the empty-lane note), and
 * carrying the same note when the entry is muted.
 */
function legendFullText(
  entry: { label: string; fullText?: string | null; muted: boolean },
  emptyLabel?: string | null,
): string | null {
  const full = chartLabelFullText(entry.label, entry.fullText)
  if (!full) {
    return null
  }
  return legendText(full, entry.muted, emptyLabel)
}

/** Hint positions of the folded categories: after the rows and the drawn categories. */
function foldedHintIndex(data: CategoricalSharePlotData, memberIndex: number): number {
  return chartLabelHintIndex(data.rows.length, 'category', data.categories.length + memberIndex)
}

/** Height per group for the slide's fixed panel: bars shrink as groups and lanes grow. */
export function groupedLayout(groupCount: number, laneCount: number, size: PlotSize, availableHeight = 470) {
  if (size !== 'slide') {
    return { labelH: 18, barH: 12, barGap: 3, groupGap: 12 }
  }
  const available = availableHeight
  const labelH = 20
  const groupGap = groupCount > 5 ? 8 : 14
  const perGroup = available / Math.max(1, groupCount) - labelH - groupGap
  const barGap = 3
  const barH = Math.max(6, Math.min(22, Math.floor(perGroup / Math.max(1, laneCount) - barGap)))
  return { labelH, barH, barGap, groupGap }
}

/** The grouped bars: a group per category, one bar per row in each. */
export function GroupedBarsGroups(props: Omit<SharedProps, 'renderRowLabel' | 'labelMinSharePct'> & {
  renderCategoryLabel?: (args: { index: number; style: React.CSSProperties }) => React.ReactNode
  availableHeight?: number
}) {
  const { data, size, locale, renderCategoryLabel, renderMode, otherOpen = false, testIdPrefix = 'grouped-bars', shareUnit } = props

  if (data.groupedBy === 'row') {
    return <GroupedByRowGroups data={data} size={size} locale={locale} renderMode={renderMode} testIdPrefix={testIdPrefix} shareUnit={shareUnit} availableHeight={props.availableHeight} />
  }
  const lanes = data.rows.filter((row) => row.bars.length > 0)
  // Opened, the groups keep the closed metrics: the box scrolls, bars never shrink.
  const layout = groupedLayout(data.categories.length, lanes.length, size, props.availableHeight)
  const fold = otherOpen ? otherFold(data) : null

  return (
    <div data-testid="grouped-bars-groups" style={{ display: 'flex', flexDirection: 'column', gap: layout.groupGap, minWidth: 0 }}>
      {data.categories.map((category, categoryIndex) => {
        const style: React.CSSProperties = { fontSize: size === 'slide' ? 16 : 13, color: TEXT, minWidth: 0, flex: 1 }
        if (fold && category.id === fold.otherId) {
          return (
            <div key={category.id} data-testid={`${testIdPrefix}-other-members`} style={{ display: 'contents' }}>
              {fold.folded.map((member, memberIndex) => (
                <FoldedGroup
                  key={member.id}
                  member={member}
                  index={memberIndex}
                  lanes={lanes}
                  layout={layout}
                  data={data}
                  size={size}
                  locale={locale}
                  renderMode={renderMode}
                  testIdPrefix={testIdPrefix}
                  shareUnit={shareUnit}
                />
              ))}
            </div>
          )
        }
        return (
          <div key={category.id} data-testid="grouped-bars-group" style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: layout.labelH, minWidth: 0 }}>
              {renderCategoryLabel ? (
                renderCategoryLabel({ index: categoryIndex, style })
              ) : (
                <ChartLabel
                  data-testid="grouped-bars-group-label"
                  text={category.label}
                  fullText={category.fullLabel}
                  hintIndex={chartLabelHintIndex(data.rows.length, 'category', categoryIndex)}
                  maxLines={size === 'slide' ? 1 : 2}
                  renderMode={renderMode}
                  title={nameTitle(category)}
                  style={style}
                />
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: layout.barGap, marginTop: 2 }}>
              {lanes.map((row) => {
                const bar = row.bars.find((entry) => entry.categoryId === category.id)
                if (!bar) {
                  return null
                }
                const printed = printedFigure(bar.share, bar.count, data.decimals, locale, shareUnit, false)
                return (
                  <div
                    key={row.id}
                    data-testid="grouped-bars-bar"
                    data-top={row.topCategoryIds.includes(category.id) ? 'true' : undefined}
                    title={`${nameTitle(row)}: ${printed}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, height: layout.barH }}
                  >
                    <div style={{ flex: 1, height: '100%', minWidth: 0 }}>
                      <div
                        style={{
                          width: `${Math.max(0, Math.min(100, bar.share))}%`,
                          minWidth: bar.share > 0 ? 2 : 0,
                          height: '100%',
                          borderRadius: '0 2px 2px 0',
                          background: category.role === 'option' ? slotColor(row.colorSlot) : slotColor(null),
                        }}
                      />
                    </div>
                    <span style={{ width: 48, flexShrink: 0, textAlign: 'right', fontSize: Math.min(12, Math.max(10, layout.barH)), color: GRAY, lineHeight: 1 }}>{printed}</span>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Grouped bars drawn a group per row (`groupedBy: row`): each row's label,
 * then one bar per category in the category's slot, so the row compares the
 * categories (the quick-study monadic themes: each theme across the
 * versions). A section header opens each stamped group; the Other row's bars
 * are neutral. A category with no figure in a row leaves an empty slot, so
 * bar N is always category N.
 */
function GroupedByRowGroups(props: {
  availableHeight?: number
  data: CategoricalSharePlotData
  size: PlotSize
  locale?: string
  renderMode?: ChartLabelRenderMode
  testIdPrefix: string
  shareUnit?: ShareUnit
}) {
  const { data, size, locale, renderMode, testIdPrefix, shareUnit } = props

  const layout = groupedLayout(data.rows.length, data.categories.length, size, props.availableHeight)
  const groups = new Map((data.groups ?? []).map((group) => [group.id, group]))

  return (
    <div data-testid="grouped-bars-groups" data-grouped-by="row" style={{ display: 'flex', flexDirection: 'column', gap: layout.groupGap, minWidth: 0 }}>
      {data.rows.map((row, rowIndex) => {
        const opened = sectionOpenedAt(data.rows, rowIndex, groups)
        const isOther = row.role === 'other'
        return (
          <React.Fragment key={row.id}>
            {opened ? <SectionHeader group={opened} testIdPrefix={testIdPrefix} /> : null}
            <div data-testid="grouped-bars-group" data-row-id={row.id} data-role={isOther ? 'other' : undefined} style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: layout.labelH, minWidth: 0 }}>
                <ChartLabel
                  data-testid="grouped-bars-group-label"
                  text={row.label}
                  fullText={row.fullLabel}
                  hintIndex={chartLabelHintIndex(data.rows.length, 'row', rowIndex)}
                  maxLines={size === 'slide' ? 1 : 2}
                  renderMode={renderMode}
                  title={nameTitle(row)}
                  style={{ fontSize: size === 'slide' ? 16 : 13, color: TEXT, minWidth: 0, flex: 1 }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: layout.barGap, marginTop: 2 }}>
                {data.categories.map((category, categoryIndex) => {
                  const bar = row.bars.find((entry) => entry.categoryId === category.id)
                  if (!bar) {
                    return <div key={category.id} data-testid={`${testIdPrefix}-slot-${rowIndex}-${categoryIndex}`} aria-hidden="true" style={{ height: layout.barH }} />
                  }
                  const printed = printedFigure(bar.share, bar.count, data.decimals, locale, shareUnit, false)
                  return (
                    <div
                      key={category.id}
                      data-testid="grouped-bars-bar"
                      data-category-id={category.id}
                      data-top={row.topCategoryIds.includes(category.id) ? 'true' : undefined}
                      title={`${nameTitle(category)}: ${printed}`}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, height: layout.barH }}
                    >
                      <div style={{ flex: 1, height: '100%', minWidth: 0 }}>
                        <div
                          style={{
                            width: `${Math.max(0, Math.min(100, bar.share))}%`,
                            minWidth: bar.share > 0 ? 2 : 0,
                            height: '100%',
                            borderRadius: '0 2px 2px 0',
                            background: isOther ? slotColor(null) : slotColor(category.colorSlot),
                          }}
                        />
                      </div>
                      <span style={{ width: 48, flexShrink: 0, textAlign: 'right', fontSize: Math.min(12, Math.max(10, layout.barH)), color: GRAY, lineHeight: 1 }}>{printed}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )
}

/**
 * One category "Other" folded, drawn as a group of lane bars (grouped-bars
 * opened). Every lane keeps its position: a lane with no figure for this
 * category leaves an empty slot, so bar N is always lane N.
 */
function FoldedGroup(props: {
  member: { id: string; label: string; fullLabel?: string }
  index: number
  lanes: Array<CategoricalRow>
  layout: ReturnType<typeof groupedLayout>
  data: CategoricalSharePlotData
  size: PlotSize
  locale?: string
  renderMode?: ChartLabelRenderMode
  testIdPrefix: string
  shareUnit?: ShareUnit
}) {
  const { member, index, lanes, layout, data, size, locale, renderMode, testIdPrefix, shareUnit } = props

  const folded = otherFold(data)?.folded ?? []

  return (
    <div data-testid={`${testIdPrefix}-other-member-${index}`} style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: layout.labelH, minWidth: 0 }}>
        <ChartLabel
          text={member.label}
          fullText={member.fullLabel}
          hintIndex={foldedHintIndex(data, index)}
          maxLines={size === 'slide' ? 1 : 2}
          renderMode={renderMode}
          title={nameTitle(member)}
          style={{ fontSize: size === 'slide' ? 16 : 13, color: TEXT, minWidth: 0, flex: 1 }}
        />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: layout.barGap, marginTop: 2 }}>
        {lanes.map((row, laneIndex) => {
          const bar = foldedRowBars(row, folded)[index]
          if (!bar) {
            return <div key={row.id} data-testid={`${testIdPrefix}-other-member-${index}-slot-${laneIndex}`} aria-hidden="true" style={{ height: layout.barH }} />
          }
          const printed = printedFigure(bar.share, bar.count, data.decimals, locale, shareUnit, false)
          return (
            <div
              key={row.id}
              data-testid={`${testIdPrefix}-other-member-${index}-bar-${laneIndex}`}
              title={`${nameTitle(row)}: ${printed}`}
              style={{ display: 'flex', alignItems: 'center', gap: 8, height: layout.barH }}
            >
              <div style={{ flex: 1, height: '100%', minWidth: 0 }}>
                <div
                  style={{
                    width: `${Math.max(0, Math.min(100, bar.share))}%`,
                    minWidth: bar.share > 0 ? 2 : 0,
                    height: '100%',
                    borderRadius: '0 2px 2px 0',
                    background: slotColor(row.colorSlot),
                  }}
                />
              </div>
              <span style={{ width: 48, flexShrink: 0, textAlign: 'right', fontSize: Math.min(12, Math.max(10, layout.barH)), color: GRAY, lineHeight: 1 }}>{printed}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/** Whether a categorical plot can open its "Other" (Python stamped what it folded). */
export function canOpenCategoricalOther(data: CategoricalSharePlotData): boolean {
  return otherFold(data) !== null && data.rows.some(rowOpensOther)
}

/**
 * The whole plot for responsive surfaces (survey tab, cards): the rows or
 * groups, then the legend, the rollup note and the page chip.
 */
export function CategoricalSharePlotView(props: {
  viewport?: PlotViewport
  plot: CategoricalSharePayload
  size?: PlotSize
  locale?: string
  renderMode?: ChartLabelRenderMode
  /** The wrapper's test id (and the truncation line's prefix); `categorical-share-<kind>` when absent. */
  testIdPrefix?: string
  /** The host's words for the Other link (decision 23); no link without them. */
  otherToggleLabels?: OtherToggleLabels | null
  /** Stacked bars: print each segment's people beside its share (a card that always printed counts). */
  showCounts?: boolean
  /** Survey tab: print stamped counts. Default percent. */
  shareUnit?: ShareUnit
  /** Survey tab: the 150ms tooltip on stacked segments. */
  fastHover?: boolean
}) {
  const { plot, size = 'report', locale, renderMode, testIdPrefix, otherToggleLabels, showCounts = false, shareUnit, fastHover = false } = props

  const rootRef = useRef<HTMLDivElement>(null)
  const graphicRef = useRef<HTMLDivElement>(null)
  const graphics = useMemo(() => [graphicRef], [])
  const budget = usePlotGraphicBudget(props.viewport, rootRef, graphics)
  const other = useOtherExpand()

  const kind = plot.kind as CategoricalShareKind
  const data = plot.data as CategoricalSharePlotData
  const truncation = data.truncation && data.labels.n_of_total_shown
    ? fillCategoricalTemplate(data.labels.n_of_total_shown, { shown: data.truncation.shown, total: data.truncation.total })
    : null
  // The (i) treats an absent mode as interactive; so does the link.
  const canOpen = isOtherExpandable(renderMode ?? 'interactive', otherToggleLabels, canOpenCategoricalOther(data))
  const otherOpen = canOpen && other.isOpen
  const otherPrefix = testIdPrefix ?? kind
  const body = (
    <>
      <div ref={graphicRef}>
      {kind === 'grouped-bars' ? (
        <GroupedBarsGroups
          data={data}
          availableHeight={budget?.height}
          size={size}
          locale={locale}
          renderMode={renderMode}
          otherOpen={otherOpen}
          testIdPrefix={data.groupedBy === 'row' ? otherPrefix : undefined}
          shareUnit={shareUnit}
        />
      ) : (
        <StackedHundredBarsRows data={data} size={size} locale={locale} renderMode={renderMode} otherOpen={otherOpen} showCounts={showCounts} shareUnit={shareUnit} fastHover={fastHover} />
      )}
      </div>
      <CategoricalShareLegend data={data} kind={kind} size={size} renderMode={renderMode} otherOpen={otherOpen} />
    </>
  )

  return (
    <div ref={rootRef} data-testid={testIdPrefix ?? `categorical-share-${kind}`} style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
      {canOpen && otherToggleLabels ? (
        <>
          <div ref={other.boxRef} id={other.boxId} style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0, ...other.boxStyle }}>
            {body}
          </div>
          <OtherToggle state={other} labels={otherToggleLabels} count={otherFold(data)?.total ?? data.categories.length} testIdPrefix={otherPrefix} />
        </>
      ) : (
        body
      )}
      {truncation ? (
        <div data-testid={testIdPrefix ? `${testIdPrefix}-truncation` : 'categorical-share-truncation'} style={{ fontSize: 11, color: GRAY }}>
          {truncation}
        </div>
      ) : null}
    </div>
  )
}
